import { HeatmapInts2D, SearchHighlighting } from '@aipb/gulliver/castor/model';
import { SearchFormMode } from '@aipb/gulliver/catalog/model';
import { DenormalizedFormat, Format, Formats } from '@aipb/gulliver/dropbox/model';
import { v4 as uiidv4 } from 'uuid';
import { HIGHLIGHT_REGEX } from './catalog-xml-parser.service';

const COMPOSITE_SEPARATOR = '|';

export function createDetailCompositeKey(coreId: string, recordId: string): string {
  return [coreId, recordId].join(COMPOSITE_SEPARATOR);
}

export function parseDetailCompositeKey(id: string): [coreId: string, recordId: string] {
  const s = id.split(COMPOSITE_SEPARATOR);
  return [s[0], s[1]];
}

export function generateSearchId() {
  return uiidv4();
}

export function createCorePermission(coreId: string) {
  return 'core_' + coreId;
}

export function isDropbox(type: string): boolean {
  return type === 'Dropbox';
}

export function buildCropRatioFromFormat(format: Format): number[] {
  const s = format.cropRatio[0].split(':');

  return [+s[0], +s[1]];
}

export function buildDenormalizedFormats(formats: Formats): DenormalizedFormat[] {
  return Object.keys(formats).map((format) => {
    const cropRatio = buildCropRatioFromFormat(formats[format]);

    return {
      ...formats[format],
      id: format,
      width: cropRatio[0],
      height: cropRatio[1]
    };
  });
}

export function calculateGridLevel(zoom: number, minZoom: number, maxZoom: number): number {
  const minGridLevel = 1;
  const maxGridLevel = 11;

  const gridLevel = Math.ceil(
    ((zoom - minZoom) / (maxZoom - 4 - minZoom)) * (maxGridLevel - minGridLevel) + minGridLevel
  );

  return gridLevel === 1 ? 2 : Math.min(maxGridLevel, gridLevel);
}

export function countRecordsInHeatmap(data: HeatmapInts2D): number {
  let count = 0;

  for (let row = 0; row < data.rows; row++) {
    if (data.counts_ints2D && data.counts_ints2D[row]) {
      for (let col = 0; col < data.columns; col++) {
        count += data.counts_ints2D[row][col];
      }
    }
  }

  return count;
}

export function normalizeMapCoordinates(coords: number[]): number[] | undefined {
  if (!coords || coords.length === 0) return undefined;

  coords[0] = Math.min(Math.max(coords[0], -180), 180);
  coords[1] = Math.min(Math.max(coords[1], -90), 90);
  coords[2] = Math.min(Math.max(coords[2], -180), 180);
  coords[3] = Math.min(Math.max(coords[3], -90), 90);

  return [coords[1], coords[0], coords[3], coords[2]];
}

export function mapHighlighting(highlighting: SearchHighlighting): SearchHighlighting {
  const highlights: string[] = [];
  const newHighlighting: SearchHighlighting = {};

  if (!highlighting) return newHighlighting;

  for (const id of Object.keys(highlighting)) {
    for (const field of Object.keys(highlighting[id])) {
      if (!(id in newHighlighting)) {
        newHighlighting[id] = {};
      }

      if (!(field in newHighlighting[id])) {
        newHighlighting[id][field] = [];
      }

      newHighlighting[id][field] = highlighting[id][field]
        .filter((h) => {
          if (highlights.includes(h)) {
            return false;
          }

          highlights.push(h);
          return true;
        })
        .map((s) => s.replace(HIGHLIGHT_REGEX, '<em>$2</em>'));

      if (newHighlighting[id][field].length === 0) {
        delete newHighlighting[id][field];
      }
    }
    if (id in newHighlighting && Object.keys(newHighlighting[id]).length === 0) {
      delete newHighlighting[id];
    }
  }

  return newHighlighting;
}

export function isSearchFormAdvanced(searchFormMode: SearchFormMode) {
  return searchFormMode === 'advanced';
}

export function isSearchFormSimple(searchFormMode: SearchFormMode) {
  return searchFormMode === 'simple';
}
